/**
 * banner.js
 * Banner — the shared top-slot celebration banner engine.
 *
 * Single implementation behind `showAchievementBanner` (quests,
 * cardbacks, recipe/first discoveries, stellar firsts, Moon Dream, foundry
 * forges). Static styling lives in styles/banner.css; every moving part runs
 * here through the Web Animations API so motion is GPU-composited
 * (transform + opacity only) and introspectable by tests.
 *
 * Contract inherited from the old main.js/foundry.js copies:
 *  - a banner is VISIBLE the moment it is shown — no spawn-hidden state, no
 *    image-settlement gate, no cross-banner promise chaining; a slow image
 *    must never swallow a celebration (multi-banner consumers stagger 4500ms)
 *  - the OUTER .achievement-banner owns fixed centering and is never
 *    animated; all motion targets .achievement-banner__body and children
 *    (the historical bug: keyframes carrying `translateY` on the outer node
 *    clobbered its `translateX(-50%)` centering mid-flight)
 *  - colliding banners hand off — the visible one fast-exits, the new one
 *    mounts immediately; nothing is queued, delayed, or dropped
 *  - motion is decorative: without WAAPI (or with reduced motion) the banner
 *    still shows, holds, and removes on the same timer schedule
 */


import { CardbackArt } from './cardback-art.js';
import { Cards } from './cards.js';
import { SoundEffects } from './sound-effects.js';
import { uiCssPx, uiPx } from './ui-scale.js';
let Banner = {};

Banner = (function () {
    const HOLD_MS = 4000;
    const REVEAL_HOLD_MS = 4200;   // forge-style reveals dwell a touch longer
    const EXIT_MS = 300;           // hold + exit stays inside the 4500ms stagger
    const HANDOFF_EXIT_MS = 240;
    const ENTER_MS = 640;
    const SPARK_COUNT = 12;

    // Damped-spring easings, precomputed (zeta/omega in the comment).
    // linear() is supported by every engine behind the platform gate; the
    // cubic-bezier fallbacks keep one soft overshoot elsewhere.
    const SPRING_ENTER = 'linear(0, 0.041, 0.1429, 0.2788, 0.4274, 0.5733, 0.7061, 0.8196, 0.9113, 0.9809, 1.03, 1.0613, 1.0781, 1.0835, 1.0808, 1.0727, 1.0616, 1.0492, 1.0369, 1.0257, 1.016, 1.0082, 1)'; // z=0.62 w=10.5
    const SPRING_POP = 'linear(0, 0.0476, 0.1678, 0.3297, 0.5069, 0.6792, 0.8324, 0.9581, 1.0525, 1.1159, 1.1511, 1.1629, 1.1567, 1.1381, 1.1122, 1.0834, 1.0551, 1.0297, 1)'; // z=0.5 w=14
    const SPRING_ENTER_FALLBACK = 'cubic-bezier(0.22, 1.18, 0.32, 1)';
    const SPRING_POP_FALLBACK = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
    const EASE_EXIT = 'cubic-bezier(0.55, 0, 0.8, 0.36)';

    let activeBanner = null;
    let springSupport = null;

    function springEasing(curve, fallback) {
        if (springSupport === null) {
            try {
                springSupport = typeof CSS !== 'undefined'
                    && typeof CSS.supports === 'function'
                    && CSS.supports('animation-timing-function', 'linear(0, 1)');
            } catch (_) {
                springSupport = false;
            }
        }
        return springSupport ? curve : fallback;
    }

    function reducedMotion() {
        if (typeof window.matchMedia !== 'function') return false;
        try {
            return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        } catch (_) {
            return false;
        }
    }

    function animateSafely(el, frames, options) {
        if (!el || typeof el.animate !== 'function') return null;
        try {
            return el.animate(frames, options);
        } catch (_) {
            return null;
        }
    }

    function ensureLiveRegion() {
        let live = document.getElementById('achievement-live-region');
        if (live) return live;
        live = document.createElement('div');
        live.id = 'achievement-live-region';
        live.className = 'achievement-live-region';
        live.setAttribute('role', 'status');
        live.setAttribute('aria-live', 'polite');
        live.setAttribute('aria-atomic', 'true');
        document.body.appendChild(live);
        return live;
    }

    function buildSparks() {
        const sparks = document.createElement('span');
        sparks.className = 'achievement-banner-sparks';
        sparks.setAttribute('aria-hidden', 'true');
        for (let i = 0; i < SPARK_COUNT; i += 1) {
            const angle = (i / SPARK_COUNT) * Math.PI * 2;
            const radius = uiPx(42 + (i % 3) * 9);
            const spark = document.createElement('span');
            spark.className = 'achievement-banner-spark';
            spark.style.setProperty('--dx', `${(Math.cos(angle) * radius).toFixed(1)}px`);
            spark.style.setProperty('--dy', `${(Math.sin(angle) * radius).toFixed(1)}px`);
            spark.style.setProperty('--spark-delay', `${(i % 4) * 45}ms`);
            sparks.appendChild(spark);
        }
        return sparks;
    }

    function buildMedia(hasImageIdentity, imageKind, reveal) {
        if (!hasImageIdentity) {
            const icon = document.createElement('span');
            icon.className = 'achievement-banner-icon';
            icon.setAttribute('aria-hidden', 'true');
            icon.textContent = '🏆';
            return { mediaEl: icon, image: null };
        }
        const image = document.createElement('img');
        image.alt = '';
        image.className = 'achievement-banner-image';
        if (imageKind === 'summoner' || !imageKind) {
            image.classList.add('achievement-banner-image--landscape');
        }
        image.loading = 'eager';
        image.decoding = 'async';
        if (!reveal) return { mediaEl: image, image };

        const frame = document.createElement('div');
        frame.className = 'achievement-banner-frame';
        frame.appendChild(image);
        frame.appendChild(buildSparks());
        return { mediaEl: frame, image };
    }

    function clearTimers(banner) {
        (banner.__moonBannerTimers || []).forEach((timer) => clearTimeout(timer));
        banner.__moonBannerTimers = [];
    }

    function addTimer(banner, fn, ms) {
        if (!banner.__moonBannerTimers) banner.__moonBannerTimers = [];
        banner.__moonBannerTimers.push(setTimeout(fn, ms));
    }

    function cancelAnimations(banner) {
        (banner.__moonBannerAnimations || []).forEach((anim) => {
            try { anim.cancel(); } catch (_) { /* no-op */ }
        });
        banner.__moonBannerAnimations = [];
    }

    function trackAnimation(banner, anim) {
        if (!anim) return;
        if (!banner.__moonBannerAnimations) banner.__moonBannerAnimations = [];
        banner.__moonBannerAnimations.push(anim);
    }

    function remove(banner) {
        clearTimers(banner);
        cancelAnimations(banner);
        banner.__moonBannerCardbackLease?.dispose?.();
        delete banner.__moonBannerCardbackLease;
        banner.querySelectorAll('.achievement-banner-image').forEach((image) => {
            Cards?.disposeCardArtImage?.(image);
        });
        if (banner.isConnected) banner.remove();
        if (activeBanner === banner) activeBanner = null;
    }

    /**
     * Play the exit and remove. Removal is timer-guaranteed: WAAPI only
     * paints the way out, it never gates the teardown.
     */
    function beginExit(banner, exitMs) {
        if (banner.__moonBannerClosing) return;
        banner.__moonBannerClosing = true;
        clearTimers(banner);
        banner.classList.add('closing');

        const body = banner.querySelector('.achievement-banner__body');
        if (body) {
            const frames = reducedMotion()
                ? [{ opacity: 1 }, { opacity: 0 }]
                : [
                    { transform: 'translateY(0) scale(1)', opacity: 1 },
                    { transform: `translateY(calc(-118% - ${uiCssPx(24)})) scale(0.97)`, opacity: 0 },
                ];
            trackAnimation(banner, animateSafely(body, frames, {
                duration: Math.max(1, exitMs - 20),
                easing: EASE_EXIT,
                fill: 'forwards',
            }));
        }
        addTimer(banner, () => remove(banner), exitMs);
    }

    function playEntrance(banner, mediaEl) {
        const body = banner.querySelector('.achievement-banner__body');
        if (!body) return;

        if (reducedMotion()) {
            trackAnimation(banner, animateSafely(body, [{ opacity: 0 }, { opacity: 1 }], {
                duration: 240,
                easing: 'ease-out',
            }));
            return;
        }

        // Transform and opacity ride separate animations: the spring wants the
        // whole flight, the fade should land early.
        trackAnimation(banner, animateSafely(body, [
            { transform: `translateY(calc(-112% - ${uiCssPx(24)})) scale(0.94)` },
            { transform: 'translateY(0) scale(1)' },
        ], { duration: ENTER_MS, easing: springEasing(SPRING_ENTER, SPRING_ENTER_FALLBACK) }));
        trackAnimation(banner, animateSafely(body, [{ opacity: 0 }, { opacity: 1 }], {
            duration: Math.round(ENTER_MS * 0.45),
            easing: 'ease-out',
        }));

        if (mediaEl) {
            trackAnimation(banner, animateSafely(mediaEl, [
                { transform: 'scale(0.55) rotate(-5deg)', opacity: 0 },
                { transform: 'scale(1) rotate(0deg)', opacity: 1 },
            ], {
                duration: 420,
                delay: 110,
                fill: 'backwards',
                easing: springEasing(SPRING_POP, SPRING_POP_FALLBACK),
            }));
        }

        const title = banner.querySelector('.achievement-banner-title');
        const subtitle = banner.querySelector('.achievement-banner-subtitle');
        [[title, 120], [subtitle, 190]].forEach(([el, delay]) => {
            if (!el) return;
            trackAnimation(banner, animateSafely(el, [
                { transform: `translateY(${uiCssPx(9)})`, opacity: 0 },
                { transform: 'translateY(0)', opacity: 1 },
            ], { duration: 340, delay, fill: 'backwards', easing: 'cubic-bezier(0.2, 0.9, 0.3, 1)' }));
        });

        const shine = banner.querySelector('.achievement-banner-shine');
        if (shine) {
            trackAnimation(banner, animateSafely(shine, [
                { transform: 'translateX(-160%) skewX(-18deg)', opacity: 0 },
                { transform: 'translateX(-90%) skewX(-18deg)', opacity: 1, offset: 0.25 },
                { transform: 'translateX(160%) skewX(-18deg)', opacity: 0 },
            ], { duration: 780, delay: 260, fill: 'backwards', easing: 'ease-in-out' }));
        }
    }

    /**
     * Show a celebration banner in the fixed top slot.
     * Legacy positional signature, plus presentation-only options:
     *   opts.reveal - forge-style reveal (framed image + spark burst), only
     *                 meaningful with an imageUrl
     *   opts.chime  - sound cue name (default 'success')
     */
    function show(title, subtitle, imageUrl, imageKind, opts) {
        if (!opts || typeof opts !== 'object') opts = {};
        const cardId = typeof opts.cardId === 'string' ? opts.cardId.trim() : '';
        const cardbackSlug = typeof opts.cardbackSlug === 'string'
            ? opts.cardbackSlug.trim()
            : '';
        const hasImageIdentity = Boolean(imageUrl || cardId || cardbackSlug);
        const reveal = opts.reveal === true && hasImageIdentity;

        const banner = document.createElement('div');
        banner.className = 'achievement-banner';
        if (reveal) banner.classList.add('achievement-banner--reveal');
        banner.setAttribute('role', 'group');
        banner.setAttribute('aria-label', [title, subtitle].filter(Boolean).join('. '));

        const body = document.createElement('div');
        body.className = 'achievement-banner__body';

        const glow = document.createElement('span');
        glow.className = 'achievement-banner-glow';
        glow.setAttribute('aria-hidden', 'true');
        body.appendChild(glow);

        const { mediaEl, image } = buildMedia(hasImageIdentity, imageKind, reveal);
        body.appendChild(mediaEl);

        const content = document.createElement('div');
        content.className = 'achievement-banner-content';
        const titleEl = document.createElement('div');
        titleEl.className = 'achievement-banner-title';
        titleEl.textContent = String(title || '');
        const subtitleEl = document.createElement('div');
        subtitleEl.className = 'achievement-banner-subtitle';
        subtitleEl.textContent = String(subtitle || '');
        content.appendChild(titleEl);
        content.appendChild(subtitleEl);
        body.appendChild(content);

        const shine = document.createElement('span');
        shine.className = 'achievement-banner-shine';
        shine.setAttribute('aria-hidden', 'true');
        body.appendChild(shine);

        banner.appendChild(body);

        // Colliding banners hand off the single top slot.
        if (activeBanner && activeBanner.isConnected) {
            beginExit(activeBanner, HANDOFF_EXIT_MS);
        }
        activeBanner = banner;

        document.body.appendChild(banner);
        ensureLiveRegion().textContent = [title, subtitle].filter(Boolean).join('. ');

        // Real image loading is best-effort and fire-and-forget — the banner
        // is already visible and must never wait on (or be swallowed by) it.
        // The shared loader keeps the exact source retryable for the banner's
        // whole lifetime; no first error may hide it or install substitute art.
        const cards = Cards;
        if (image) {
            try {
                const loadDirect = (source) => {
                    if (!source || image.dataset.bannerCardbackSource === source) return null;
                    cards?.disposeCardArtImage?.(image);
                    image.dataset.bannerCardbackSource = source;
                    return cards?.loadCardArtImage?.(
                        image,
                        source,
                        { fit: 'cover' },
                        { directArtOnly: true },
                    );
                };
                const loading = cardbackSlug
                    ? loadDirect(imageUrl)
                    : cardId && typeof cards?.resolveCardArt === 'function'
                    ? cards.resolveCardArt(
                        { id: cardId, imageUrl },
                        image,
                        { directArtOnly: false },
                    )
                    : cards?.loadCardArtImage?.(
                        image,
                        imageUrl,
                        { fit: 'cover' },
                        { directArtOnly: imageKind !== 'card' },
                    );
                Promise.resolve(loading).catch(() => {});
                if (cardbackSlug && typeof CardbackArt?.observe === 'function') {
                    banner.__moonBannerCardbackLease = CardbackArt.observe(
                        image,
                        cardbackSlug,
                        (descriptor) => {
                            if (!banner.isConnected || descriptor?.kind === 'custom-pending') return;
                            try {
                                Promise.resolve(loadDirect(descriptor?.url)).catch(() => {});
                            } catch (_) { /* Exact retry remains presentation-only. */ }
                        },
                    );
                }
            } catch (_) {
                // The visible celebration is independent from media recovery.
            }
        }

        SoundEffects?.play?.(typeof opts.chime === 'string' ? opts.chime : 'success');

        playEntrance(banner, mediaEl);
        addTimer(banner, () => beginExit(banner, EXIT_MS), reveal ? REVEAL_HOLD_MS : HOLD_MS);
        return banner;
    }

    function dismissAll() {
        document.querySelectorAll('.achievement-banner').forEach((banner) => remove(banner));
        activeBanner = null;
    }

    /** Shared motion vocabulary for sibling transient surfaces. */
    function easings() {
        return {
            enter: springEasing(SPRING_ENTER, SPRING_ENTER_FALLBACK),
            pop: springEasing(SPRING_POP, SPRING_POP_FALLBACK),
            exit: EASE_EXIT,
        };
    }

    return { show, dismissAll, easings };
})();

const showAchievementBanner = function (title, subtitle, imageUrl, imageKind, opts) {
    return Banner.show(title, subtitle, imageUrl, imageKind, opts || {});
};

export { Banner };

export { showAchievementBanner };
